import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { CheckCircle2, CreditCard, Landmark, Lock, AlertTriangle, ShieldCheck, Wallet } from 'lucide-react';
import { RatenFortschritt, RatenStatusChip, Spinner, eur } from '../components/RatenShared';
import { ErgebnisPanel } from '../components/RatenInfoParts';
import { Vorvertraglich } from '../components/raten/RatenWiderruf';
import { ZahlartWahl } from '../components/zahlung/ZahlartWahl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Hinweis = ({ Icon, text }) => (
  <div className="flex gap-3 p-4 bg-[#f5f8fa] rounded-sm text-xs text-[#34536b] leading-relaxed"><Icon className="w-4 h-4 text-[#b8893d] flex-shrink-0 mt-0.5" />{text}</div>
);

export default function Ratenzahlung() {
  const { t } = useTranslation();
  const { planId } = useParams();
  const [params] = useSearchParams();
  const key = params.get('key') || '';
  const sessionId = params.get('session_id') || '';
  const cancelled = params.get('cancelled') === '1';
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(!!sessionId);
  const [agree, setAgree] = useState(false);
  const [schuld, setSchuld] = useState(false);
  const [vorv, setVorv] = useState(false);
  const [zahlart, setZahlart] = useState(null);

  const load = async () => {
    try { const r = await axios.get(`${API}/raten/${planId}`, { params: { key } }); setPlan(r.data); }
    catch (e) { setError(e?.response?.data?.detail || t('raten.pay.link_ungueltig')); }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [planId, key]);

  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    const h = setInterval(async () => {
      tries += 1;
      try {
        const r = await axios.get(`${API}/raten/${planId}/status`, { params: { key, session_id: sessionId } });
        if (r.data.aktiv || tries > 10) { clearInterval(h); setPolling(false); load(); }
      } catch (e) { if (tries > 10) { clearInterval(h); setPolling(false); } }
    }, 2000);
    return () => clearInterval(h);
  }, [sessionId, planId, key]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkout = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/raten/${planId}/checkout`, { key, origin_url: window.location.origin, vereinbarung: agree, schuldanerkenntnis: schuld, vorvertraglich: vorv, zahlart });
      window.location.href = r.data.checkout_url;
    } catch (e) { setError(e?.response?.data?.detail || t('raten.pay.fehler')); setBusy(false); }
  };

  const b = plan?.berechnung;
  const aktiv = plan && ['aktiv', 'ueberfaellig', 'abgeschlossen'].includes(plan.status);
  const ref = plan ? [plan.rechnungsnummer && `${t('raten.kond.rechnungsnummer')} ${plan.rechnungsnummer}`, plan.aktenzeichen && `${t(`raten.form.referenz_${plan.referenz_typ || 'aktenzeichen'}`)} ${plan.aktenzeichen}`, plan.mandantennummer && `${t('raten.form.referenz_mandantennummer')} ${plan.mandantennummer}`].filter(Boolean) : [];

  return (
    <div className="min-h-screen bg-[#f5f8fa]">
      <Seo title={`${t('raten.pay.titel')} – Kanzlei Nabhan Gäbler & Partner`} path={`/ratenzahlung/${planId}`} noindex />
      <Header />
      <main className="pt-[150px] pb-20">
        <section className="bg-[#1B3A5C] text-white py-14 relative overflow-hidden">
          <div className="absolute -bottom-24 right-0 w-96 h-96 bg-[#b8893d] rounded-full blur-3xl opacity-10 pointer-events-none"></div>
          <div className="max-w-6xl mx-auto px-6 lg:px-10 relative">
            <div className="flex items-center gap-3 text-[11px] tracking-[0.3em] text-[#e0b56a] uppercase mb-3"><span className="w-10 h-px bg-[#e0b56a]"></span>{t('raten.pay.kicker')}</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light" data-testid="raten-pay-title">{t('raten.pay.titel')}</h1>
            <p className="text-white/75 mt-3 text-sm sm:text-base max-w-2xl">{t('raten.pay.untertitel')}</p>
          </div>
        </section>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 -mt-8">
          {error && <div className="bg-white border-l-4 border-rose-500 p-5 text-sm text-rose-700 flex gap-2 rounded-sm shadow-sm" data-testid="raten-pay-error"><AlertTriangle className="w-4 h-4 mt-0.5" />{error}</div>}
          {!plan && !error && <div className="bg-white p-10 flex justify-center rounded-sm shadow-sm"><Spinner /></div>}
          {plan && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-3 bg-white border border-gray-100 rounded-sm shadow-[0_1px_2px_rgba(27,58,92,0.06)] p-6 sm:p-8" data-testid="raten-pay-card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] tracking-[0.28em] text-[#b8893d] uppercase">{plan.betreff || t('raten.pay.honorar')}</div>
                    <div className="font-serif text-2xl md:text-3xl text-[#1B3A5C] mt-1">{eur(b.gesamtbetrag)}</div>
                    <div className="text-sm text-[#7a8a99] mt-1" data-testid="raten-pay-ref">{ref.join(' · ') || plan.plan_id}</div>
                  </div>
                  <RatenStatusChip status={plan.status} label={t(`raten.status.${plan.status}`)} />
                </div>

                {polling && !aktiv && <div className="mt-6 p-4 bg-[#fbf7f0] border border-[#e5d8b8] text-sm text-[#8a6d1a] flex items-center gap-3 rounded-sm" data-testid="raten-pay-polling"><Spinner /> {t('raten.pay.pruefen')}</div>}
                {cancelled && !aktiv && <div className="mt-6 p-4 bg-gray-50 border border-gray-200 text-sm text-[#34536b] rounded-sm" data-testid="raten-pay-cancelled">{t('raten.pay.abgebrochen')}</div>}
                {aktiv && (
                  <div className="mt-6 p-5 bg-emerald-50 border-l-4 border-emerald-600 text-sm text-emerald-900 flex gap-3 rounded-sm" data-testid="raten-pay-success">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-medium">{plan.status === 'abgeschlossen' ? t('raten.pay.abgeschlossen') : t('raten.pay.eingerichtet')}</div>
                      <div className="mt-1 text-emerald-800/80">{t('raten.pay.eingerichtet_text')}</div>
                      <div className="mt-3"><RatenFortschritt raten={plan.raten} /><div className="text-xs mt-1">{t('raten.pay.bezahlt_von', { done: plan.raten.filter((r) => r.status === 'bezahlt').length, total: plan.raten.length, offen: eur(plan.offener_betrag) })}</div></div>
                    </div>
                  </div>
                )}

                {plan.zahlbar && !polling && (
                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="raten-pay-methods">
                      <Hinweis Icon={Landmark} text={t('raten.pay.sepa_hinweis')} />
                      <Hinweis Icon={CreditCard} text={t('raten.pay.karte_hinweis')} />
                      <Hinweis Icon={Wallet} text={t('raten.pay.paypal_hinweis')} />
                    </div>
                    <Vorvertraglich rows={plan.vorvertraglich} info={plan.widerrufsinfo} checked={vorv} onChange={setVorv} />
                    <label className="flex items-start gap-3 text-xs text-[#34536b] cursor-pointer mb-4">
                      <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#b8893d]" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="raten-pay-agree" />
                      <span>{t('raten.pay.zustimmung')} <Link to="/agb" target="_blank" className="text-[#b8893d] underline">{t('raten.pay.agb')}</Link> · <Link to="/datenschutz" target="_blank" className="text-[#b8893d] underline">{t('raten.pay.datenschutz')}</Link></span>
                    </label>
                    <label className="flex items-start gap-3 text-xs text-[#34536b] cursor-pointer mb-6 p-4 bg-[#fbf7f0] border border-[#e5d8b8] rounded-sm">
                      <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#b8893d] flex-shrink-0" checked={schuld} onChange={(e) => setSchuld(e.target.checked)} data-testid="raten-pay-schuldanerkenntnis" />
                      <span><span className="font-medium text-[#1B3A5C]">{t('raten.pay.schuld_titel')}</span><br />{t('raten.pay.schuld_text', { betrag: eur(b.gesamtbetrag), ref: ref.join(', ') || plan.plan_id })}</span>
                    </label>
                    <ZahlartWahl betrag={b.sofort_faellig || b.rate || b.gesamtbetrag} zweck="raten" wahl={zahlart} onWahl={setZahlart} testId="raten-zahlart" hinweis="Die gewählte Zahlart gilt für die Anzahlung und alle monatlichen Raten – ohne Aufschlag." />
                    <button type="button" onClick={checkout} disabled={busy || !agree || !schuld || !vorv} data-testid="raten-pay-checkout" className="w-full inline-flex items-center justify-center gap-2 bg-[#b8893d] hover:bg-[#a07730] disabled:opacity-50 text-white text-xs tracking-[0.22em] px-6 py-4 rounded-sm transition-colors">
                      {busy ? <Spinner /> : <Lock className="w-4 h-4" />} {t('raten.pay.button')}
                    </button>
                    <div className="mt-4 flex items-center gap-2 text-[11px] text-[#7a8a99]"><ShieldCheck className="w-3.5 h-3.5 text-[#b8893d]" />{t('raten.info.zahlarten')}</div>
                    <p className="text-[11px] text-[#7a8a99] mt-3 leading-relaxed">{t('raten.pay.rechtshinweis')}</p>
                  </div>
                )}
                {!plan.zahlbar && !aktiv && <p className="mt-6 text-sm text-[#7a8a99]" data-testid="raten-pay-nicht-zahlbar">{t('raten.pay.nicht_zahlbar')}</p>}
              </div>
              <div className="lg:col-span-2 order-first lg:order-none lg:sticky lg:top-40 self-start lg:max-h-[calc(100vh-11rem)] lg:overflow-y-auto">
                <ErgebnisPanel vorschau={b} laufzeit={b.laufzeit_monate} raten={plan.raten} kicker={t('raten.pay.rate_kicker')} disclaimer={false} defaultOffen={aktiv} />
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
